// T007 — EP-86 Scenario 1: Per-page dismissible coaching banner.
// Renders only when ?coach=explore is in the URL and the pageKey has not been
// dismissed. Dismissed keys are stored in localStorage under
// `explore_coach_dismissed` (a JSON array). Dismissal persists across reloads.
//
// Note (S1-2): dismissal is per-browser, not per-account, so it may leak
// across accounts sharing a browser. Acceptable for v1.
import { useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Sparkles, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'

export type ExploreCoachPageKey = 'property' | 'rooms' | 'tenant' | 'lease' | 'invoice'

export interface ExploreCoachBannerProps {
  pageKey: ExploreCoachPageKey
}

const STORAGE_KEY = 'explore_coach_dismissed'

function getDismissedKeys(): ExploreCoachPageKey[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as ExploreCoachPageKey[]) : []
  } catch {
    return []
  }
}

function addDismissedKey(key: ExploreCoachPageKey): void {
  const current = getDismissedKeys()
  if (!current.includes(key)) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...current, key]))
  }
}

export function ExploreCoachBanner({ pageKey }: ExploreCoachBannerProps) {
  const { t } = useTranslation('onboarding')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const coachParam = searchParams.get('coach')

  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (coachParam !== 'explore') return true
    return getDismissedKeys().includes(pageKey)
  })

  const handleDismiss = useCallback(() => {
    addDismissedKey(pageKey)
    setDismissed(true)
  }, [pageKey])

  // EP-158 AC-9 (F12): always back to the Explorer hub, never the browser's
  // history stack — a user who followed a different link onto this page (not
  // "Review" from the hub) would otherwise be sent somewhere unrelated.
  const handleBack = useCallback(() => {
    navigate('/onboarding/explore')
  }, [navigate])

  // Self-hide when the param is absent or when already dismissed.
  if (coachParam !== 'explore' || dismissed) return null

  return (
    <div
      className="alert alert-info mb-4"
      role="note"
      data-testid="explore-coach-banner"
    >
      <Sparkles className="w-5 h-5 shrink-0" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">
          {t(`coach.${pageKey}.title`)}
        </p>
        <p className="text-sm mt-0.5">
          {t(`coach.${pageKey}.body`)}
          {' '}
          <button
            type="button"
            className="link link-hover font-medium"
            onClick={handleBack}
          >
            {t('coach.back_to_setup')}
          </button>
        </p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        shape="circle"
        className="shrink-0"
        aria-label={t('coach.dismiss')}
        onClick={handleDismiss}
        data-testid="explore-coach-dismiss"
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  )
}
